import * as React from 'react'
import { ArticleBusinessType, InventoryPool } from 'admin/articleService'
import { useGetAllArticles, useGetAllCategories } from 'products/redux'
import { TimeSlotGroup } from 'timeSlots/timeSlotsService'
import { useGetCapacitiesQuery } from 'capacity/reduxQuery'
import { useTranslation } from 'translations'
import { CapacitySectionShell } from './components/sectionShell'
import { SectionPart } from './components/sectionPart'
import { productCapacityPart, productTimeSlotsPart } from './model/sectionState'
import { articleCategoryIds, productCapacityRows } from './model/capacityRows'
import { categoriesAllSwitchedOff, productGroupBlocks } from './model/groupBlocks'
import { useGroupBlocks } from './useGroupBlocks'

interface ProductCapacitySectionProps {
    accountSlug: string
    productId: string
    businessType: ArticleBusinessType | null
    usedResources: InventoryPool[]
    // True while the form is still fetching the product: usedResources is empty until then.
    loading?: boolean
}

export function ProductCapacitySection({
    accountSlug,
    productId,
    businessType,
    usedResources,
    loading = false,
}: ProductCapacitySectionProps) {
    const { t } = useTranslation()
    const isNew = productId === 'new'
    const articles = useGetAllArticles()
    const categories = useGetAllCategories()
    const capacities = useGetCapacitiesQuery(accountSlug, { skip: isNew })

    const article = React.useMemo(() => articles.find((entry) => entry.id === productId), [articles, productId])
    const isBundle = !!article?.isBundle || businessType === 'bundle'
    const categoryIds = React.useMemo(() => (article ? articleCategoryIds(article) : []), [article])
    const resolve = React.useCallback(
        (groups: TimeSlotGroup[]) => {
            const { blocks, dependsOnCategory } = productGroupBlocks({ id: productId, categoryIds }, groups, categories)
            return { blocks, lead: dependsOnCategory ? t('capacity.formSection.viaCategory') : undefined }
        },
        [productId, categoryIds, categories, t],
    )
    const timeSlots = useGroupBlocks(accountSlug, isNew, resolve)

    const bookingWindow = timeSlots.resolved ? (timeSlots.resolved.blocks.length > 0 ? 'slot' : 'day') : null
    const rows = productCapacityRows({
        accountSlug,
        productId,
        businessType,
        isBundle,
        usedResources,
        capacities: capacities.data,
        categories,
        window: bookingWindow,
    })

    const capacityState = productCapacityPart(
        { isNew, loading: loading || capacities.isLoading, failed: capacities.isError, rows },
        t,
    )
    const timeSlotsState = productTimeSlotsPart(
        {
            accountSlug,
            isNew,
            isBundle,
            failed: timeSlots.failed,
            resolved: timeSlots.resolved,
            switchedOff: categoriesAllSwitchedOff(categoryIds, categories),
        },
        t,
    )

    return (
        <CapacitySectionShell
            capacities={<SectionPart state={capacityState} accountSlug={accountSlug} />}
            timeSlots={<SectionPart state={timeSlotsState} accountSlug={accountSlug} details={timeSlots.details} />}
        />
    )
}
